import { CommonModule } from "@angular/common";
import { HostListener } from '@angular/core';
import { Component, computed, DestroyRef, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faSave } from "@fortawesome/free-solid-svg-icons";

import { PageComponent } from "../page/page.component";
import { Question, Questionnaire } from "../models";
import { Progress } from "../progress/progress";
import { Themes } from "../themes/themes";
import { ApiService } from "../services/api.service";
import { AuthService } from "../services/auth.service";
import { ToastService } from "../services/toast.service";

interface QuestionAnswers { [subquestionId: string]: string };
@Component({
    selector: 'fgai-questionnaire',
    imports: [CommonModule, PageComponent, Progress, Themes, FontAwesomeModule],
    templateUrl: './questionnaire.html',
    styleUrl: './questionnaire.scss',
})
export class QuestionnaireComponent {
    faSave = faSave;

    questionnaire?: Questionnaire;
    readonly = false;

    /**
     * Are we currently saving the answers to the database?
     */
    saving = signal<boolean>(false);
    /**
     * Do we have any unsaved changes?
     */
    changed = signal<boolean>(false);
    currentPageNum = signal(1);
    progress: { [number: number]: boolean } = { 1: true };
    questions = signal<Question[]>([]);
    lastQuestion = computed(() => this.questions().length);
    /**
     * The loaded answers, which might be edited by the user
     */
    answerCache: { [questionId: string]: QuestionAnswers } = {};
    /**
     * The loaded difference explanation, which might be edited by the user
     */
    explainDiffCache: { [questionId: string]: string } = {};
    /**
     * The next page being shown is after the current page
     */
    forwards = true;
    public user$ = this.authService.currentUser$;
    userId: number = 0;
    hideExplainDiff = signal<boolean>(false);

    /** The constructor determines whether to create a new questionnaire  */
    constructor(activatedRoute: ActivatedRoute, private apiService: ApiService, private authService: AuthService, private destroyRef: DestroyRef, private router: Router, private toastService: ToastService) {
        activatedRoute.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(p => {
            this.hideExplainDiff.set(p.get('hide_diff') === '1');
        });
        activatedRoute.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(p => {
            if (p['id']) {
                this.retrieveQuestionnaire(p['id']);
            }
        });
    }

    /**
     * Gets the latest version of a specific question
     * @param id the questionnaire ID
     * @param pageNum the page number to retrieve
     */
    private async retrieveQuestion(id: string, pageNum: number) {
        if (!this.questionnaire) {
            // we don't have the questionnaire yet, so we have to get everything
            this.retrieveQuestionnaire(id);
            return;
        }

        if (pageNum < 1 || pageNum > this.questionnaire.questions.length) {
            console.error(`${pageNum} out of bounds error`, this.questionnaire.questions);
            return;
        }

        const questionsWrapper = this.apiService.createQuestionsWrapper(id);
        const question = await questionsWrapper.get(this.questionnaire.questions[pageNum - 1].id);
        const questions = this.questionnaire.questions;

        this.updateQuestionCache(question);

        this.questions.set([
            ...questions.slice(0, pageNum - 1),
            question,
            ...questions.slice(pageNum)]);
        this.changed.set(false);
    }

    private async retrieveQuestionnaire(id: string) {
        const questionnaire = await this.apiService.questionnaires.get(id);
        const questionsWrapper = this.apiService.createQuestionsWrapper(questionnaire.id);
        const questions = await Promise.all(questionnaire.questions.map(
            question => questionsWrapper.get(question.id)
        ));
        this.questionnaire = questionnaire;
        this.questions.set(questions);

        // restore the progress
        for (const question of questions) {
            this.progress[question.sequence_number] = this.questionAnswered(question);

            // initialize the cache
            this.updateQuestionCache(question);
        }

        // don't change it after approval
        this.readonly = this.questionnaire.status === 'APPROVED';
    }

    private updateQuestionCache(question: Question) {
        // always set this to the version just retrieved from the database
        this.answerCache[question.id] = Object.fromEntries(
            question.subquestions.map(sq => [sq.id, sq.content]));
        this.explainDiffCache[question.id] = question.explain_diff;
    }

    private questionAnswered(question: Question) {
        for (const sq of question.subquestions) {
            if (sq.answer?.content) {
                return true;
            }
        }

        return false;
    }

    goToQuestion(currentQuestion: Question, destNum: number) {
        this.saveQuestion(currentQuestion);
        this.updateProgress();
        this.setPageNum(destNum);
    }

    complete(lastQuestion: Question) {
        if (this.questionnaire) { this.questionnaire.status = "PENDING_APPROVAL" } else { return };
        this.apiService.questionnaires.update(this.questionnaire);
        this.saveQuestion(lastQuestion);
        this.router.navigate(['dashboard']);
    }

    approve(lastQuestion: Question) {
        this.saveQuestion(lastQuestion);
        if (this.questionnaire) {
            if (confirm($localize`Wil je dit functioneringsgesprek goedkeuren? Het kan hierna niet meer gewijzigd worden.`)) {
                this.questionnaire.status = "APPROVED";
                this.apiService.questionnaires.update(this.questionnaire);
                this.router.navigate(['dashboard']);
            }
        }

    }

    /**
     * This method has two purposes:
     *   1. It updates the questionnaire status to In Progress if this is the first question answered
     *   2. It updates the pageNum and progress variables
     */
    private updateProgress() {
        if (this.questionnaire && this.questionnaire.status == "NOT_STARTED") {
            this.questionnaire.status = "IN_PROGRESS";
            this.apiService.questionnaires.update(this.questionnaire);
        }

        const currentQuestion = this.questions()[this.currentPageNum() - 1];

        this.progress = {
            ...this.progress,
            [this.currentPageNum()]: this.questionAnswered(currentQuestion)
        };
    }

    updateAnswers(answers: QuestionAnswers, currentQuestion: Question) {
        if (this.answersChanged(this.answerCache[currentQuestion.id], answers)) {
            this.changed.set(true);
            this.answerCache[currentQuestion.id] = answers;
        }
    }

    /**
     * Checks if the content of the answers for a question changed
     * @param current the current answers for a question
     * @param updated the possibly updated answers for a question
     * @returns
     */
    private answersChanged(current: QuestionAnswers, updated: QuestionAnswers): boolean {
        if (!current || Object.keys(current).length !== Object.keys(updated).length) {
            return true;
        }

        for (const [subquestionId, answer] of Object.entries(current)) {
            if (updated[subquestionId] !== answer) {
                return true;
            }
        }

        return false;
    }

    updateExplainDiff(text: string, currentQuestion: Question) {
        if (this.explainDiffCache[currentQuestion.id] !== text) {
            this.changed.set(true);
            this.explainDiffCache[currentQuestion.id] = text;
        }
    }

    setPageNum(pageNum: number) {
        if (!this.canDeactivate()) {
            return;
        }
        this.currentPageNum.set(pageNum);
        if (this.questionnaire) {
            this.retrieveQuestion(this.questionnaire.id, pageNum);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    async saveQuestion(question: Question) {
        if (this.readonly || !this.changed()) {
            // no need to save when nothing (can be) changed
            return;
        }

        this.saving.set(true);
        if (!this.questionnaire) {
            alert('Questionnaire object is missing; unable to save');
            return;
        }

        if (question.id in this.answerCache) {
            question.explain_diff = this.explainDiffCache[question.id];
            const answerArray: Array<boolean> = [];
            for (const sq of question.subquestions) {
                sq.answer = {
                    id: '',
                    // only content is required;
                    // the serializer should only store the answer if it changed
                    // and determine the user, date and the parent subquestion object
                    // is already encoded in the structure
                    content: this.answerCache[question.id][sq.id] ?? '',
                    subquestion: sq.id,
                    user: 0,
                    date_created: new Date(),
                    date_edited: new Date()
                };
                if (sq.answer.content) answerArray.push(true);
            }
            if (answerArray.length == question.subquestions.length) {
                question.completion = "COMPLETED"
            } else if (answerArray.length > 0) {
                question.completion = "PARTIAL"
            } else {
                question.completion = "EMPTY"
            }
            const questionWrapper = this.apiService.createQuestionsWrapper(this.questionnaire.id);
            await questionWrapper.update(question);
            this.toastService.show({
                header: `Antwoord opgeslagen`,
                body: `Uw antwoord is opgeslagen.`,
                type: "success",
                delay: 1500
            });
        }
        this.saving.set(false);
        this.changed.set(false);
        this.retrieveQuestionnaire(this.questionnaire?.id);
    }

    // @HostListener allows us to also guard against browser refresh, close, etc.
    @HostListener('window:beforeunload')
    canDeactivate(): boolean {
        return this.saving() ||
            !this.changed() ||
            confirm('PAS OP: U heeft wijzigingen die nog niet zijn opgeslagen. Klik op OK als u de pagina toch wil verlaten.');
    }

}
